import React, { useLayoutEffect, useRef, useState } from 'react';
import { Checkbox } from 'react-bootstrap';
import { useField } from 'react-final-form';
import {
  ALL_OPTIONS,
  DUPLICATION_OPTIONS,
  duplicationText,
  getDuplicationWarnings,
  optionLabel,
} from '../../../constants/initiativeDuplication.js';
import QuestionBlueIcon from '../../icons/QuestionBlueIcon.jsx';
import WhatGetsCopiedTooltip from './WhatGetsCopiedTooltip.jsx';
import projectStyles from '../../projects/duplicate/DuplicateProjectModal.module.css';
import styles from './DuplicateInitiativeModal.module.css';

const TOOLTIP_ID = 'what-gets-copied-initiative-tooltip';

const toggleValue = (values, value) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

// Keeps the order of the grid, so the request does not depend on the click order.
const inOrder = (values) =>
  ALL_OPTIONS.filter((value) => values.includes(value));

// Gap between the icon and the tooltip + minimal distance to the viewport edge.
const TOOLTIP_OFFSET = 4 + 8;

const WhatToCopyField = () => {
  const { input } = useField('options');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState('top');
  const tooltipIconRef = useRef(null);
  const tooltipRef = useRef(null);

  const openTooltip = () => setTooltipOpen(true);
  const closeTooltip = () => {
    setTooltipOpen(false);
    setTooltipPlacement('top');
  };

  // Measured before paint: opens above the icon when it fits there, otherwise below
  // (or on the side with more room), so it is never clipped at the top of the viewport.
  useLayoutEffect(() => {
    if (!tooltipOpen || !tooltipRef.current || !tooltipIconRef.current) return;

    const icon = tooltipIconRef.current.getBoundingClientRect();
    const height = tooltipRef.current.offsetHeight + TOOLTIP_OFFSET;
    const spaceAbove = icon.top;
    const spaceBelow = window.innerHeight - icon.bottom;
    const fitsAbove = height <= spaceAbove;

    setTooltipPlacement(
      fitsAbove || (height > spaceBelow && spaceAbove >= spaceBelow)
        ? 'top'
        : 'bottom'
    );
  }, [tooltipOpen]);

  const options = input.value || [];
  const setOptions = (values) => input.onChange(inOrder(values));
  const allSelected = options.length === ALL_OPTIONS.length;

  const warnings = getDuplicationWarnings(options);

  return (
    <div className={projectStyles.whatToCopy}>
      <div className={`flex ${projectStyles.whatToCopyHeader}`}>
        <div className="flex what_to_copy">
          <span className={`control-label ${projectStyles.label}`}>
            {duplicationText('whatToCopy')}
          </span>
          <div
            className={projectStyles.tooltipWrapper}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
            onFocus={openTooltip}
            onBlur={closeTooltip}
            onKeyDown={(e) => e.key === 'Escape' && closeTooltip()}
          >
            <button
              type="button"
              ref={tooltipIconRef}
              className={`btn-clear bg-transparent ${projectStyles.iconButton}`}
              aria-label={duplicationText('whatGetsCopied')}
              aria-describedby={tooltipOpen ? TOOLTIP_ID : undefined}
              onClick={openTooltip}
            >
              <QuestionBlueIcon />
            </button>

            {tooltipOpen ? (
              <div
                id={TOOLTIP_ID}
                ref={tooltipRef}
                role="tooltip"
                className={`${styles.tooltip} ${
                  tooltipPlacement === 'top'
                    ? styles.tooltipTop
                    : styles.tooltipBottom
                }`}
              >
                <WhatGetsCopiedTooltip />
              </div>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className={`btn-clear bg-transparent text-primary ${projectStyles.linkButton}`}
          onClick={() => setOptions(allSelected ? [] : ALL_OPTIONS)}
        >
          {duplicationText(allSelected ? 'clearAll' : 'selectAll')}
        </button>
      </div>

      <div className={`what_to_copy_box ${projectStyles.sectionsBox}`}>
        <div className={projectStyles.grid}>
          {DUPLICATION_OPTIONS.map(({ value, key }) => (
            <Checkbox
              key={value}
              checked={options.includes(value)}
              onChange={() => setOptions(toggleValue(options, value))}
            >
              <span className="checkmark"></span>
              <span className="text-small">{optionLabel(key)}</span>
            </Checkbox>
          ))}
        </div>
      </div>

      {warnings.length ? (
        <div className={projectStyles.warnings} role="status">
          {warnings.map(({ key, title, body }) => (
            <p key={key}>
              {title ? <strong>{title}</strong> : null}
              {title ? ' ' : null}
              {body}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default WhatToCopyField;
